import React, { useMemo, useEffect, useState, useRef } from "react";
import "./styles.css";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, IDatasource, IGetRowsParams } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import useRouter from "@/store/useRouter";
import { useShallow } from "zustand/shallow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faUser, faClock, faArrowLeft, faUpload } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { AG_GRID_LOCALE_CZ } from '@ag-grid-community/locale';
import { UiColumn2 } from "../ItemList/indexOld";
import Card from "../Card";
import AppUserCard from "../AppUser/appUserCard";
import StockCard from "../Stock/stockOdmraz";
import ListsSettings from "../ListsSettings";
import ExcelJS from "exceljs";
import DialogWindow from "../DialogWindow";
import useAppData from "@/store/useAppData";
import { List, SettingsScope, Stock, User } from "./types";
import AppUserCardOdmraz from "../AppUser/appUserCardOdmraz";
import AirlineCard, { Airline } from "../Airline/airlineCard";

const myTheme = themeQuartz.withParams({
    fontSize: 17,
    headerFontSize: 17,
    rowHeight: 55,
    headerHeight: 48,
});

const userSettingsOdmraz: React.FC = () => {

    const setDisplaySettings = useAppData(state => state.setDisplaySettings);
    const selectedModule = useAppData(state => state.selectedModule);

    const columnsUsers: UiColumn2<User>[] = [
        { field: "id", headerName: "Id", flex: 1, filter: 'agTextColumnFilter', floatingFilter: true },
        { field: "name", headerName: "Jméno", flex: 2, filter: 'agTextColumnFilter', floatingFilter: true },
        { field: "login", headerName: "Login", flex: 2, filter: 'agTextColumnFilter', floatingFilter: true },
        { field: "email", headerName: "Email", flex: 2, filter: 'agTextColumnFilter', floatingFilter: true },
        {
            field: "userrole",
            headerName: "Role",
            flex: 2,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            valueFormatter: params => {
                if (!params.value) return "";

                if (params.value === "admin") return "Administrátor";
                if (params.value === "edit") return "Editace";
                if (params.value === "viewer") return "Prohlížení";
            }
        },
        {
            field: "active", headerName: "Aktivní", flex: 1, minWidth: 100,
            cellDataType: false,
            valueFormatter: params => params.value ? "Ano" : "Ne",
        },
    ]

    const columnsStocks: UiColumn2<Stock>[] = [
        { field: "id", headerName: "Id", flex: 1, filter: 'agTextColumnFilter', floatingFilter: true },
        { field: "name", headerName: "Název", flex: 2, filter: 'agTextColumnFilter', floatingFilter: true },
        { field: "SAPInvMajetek", headerName: "SAPInvMajetek", flex: 2, filter: 'agTextColumnFilter', floatingFilter: true },
        { field: "SAPOznaceniIM", headerName: "SAPOznaceniIM", flex: 2, filter: 'agTextColumnFilter', floatingFilter: true },
        {
            field: "main",
            headerName: "Hlavní",
            flex: 0.7,
            minWidth: 70,
            // filter: "set",
            // floatingFilter: true,
            valueFormatter: params => params.value ? "Ano" : "Ne"
        },
        {
            field: "twoOperators",
            headerName: "Více operátorů",
            flex: 0.7,
            minWidth: 70,
            // filter: "set",
            // floatingFilter: false,
            valueFormatter: params => params.value ? "Ano" : "Ne"
        }
    ]

    const columnsAirlines: ColDef<Airline>[] = [
        { field: "id", headerName: "Id", flex: 1, filter: "agNumberColumnFilter", floatingFilter: true },
        { field: "name", headerName: "Název", flex: 2, filter: "agTextColumnFilter", floatingFilter: true },
        { field: "code", headerName: "Kód společnosti", flex: 1, filter: "agTextColumnFilter", floatingFilter: true },
        { field: "sapCode", headerName: "SAP kód", flex: 1, filter: "agTextColumnFilter", floatingFilter: true },
        { field: "email", headerName: "E-mail", flex: 2, filter: "agTextColumnFilter", floatingFilter: true }
    ];

    const gridApiRef = useRef<GridApi | null>(null);
    const fileImportEmployesInputRef = useRef(null);

    const router = useRouter(
        useShallow(state => ({
            go: state.go,
            history: state.history
        }))
    );

    const actions: FloatingAction[] = [
        {
            key: "1",
            label: "Import zaměstnanců",
            icon: <FontAwesomeIcon icon={faUpload} />,
            onClick: () => fileImportEmployesInputRef.current.click()
        },
        {
            key: "1",
            label: "Zpět",
            icon: <FontAwesomeIcon icon={faArrowLeft} />,
            onClick: () => { setDisplaySettings(false); router.go({ key: RouterStoreItemKey.LISTITEMS }); }
        }
    ]

    const [usersState, setUsersState] = React.useState<User[]>([]);
    const [stocks, setStocks] = React.useState<Stock[]>([]);
    const [airlines, setAirlines] = useState<Airline[]>([]);
    const [displayAirlineForm, setDisplayAirlineForm] = useState(false);
    const [lists, setLists] = React.useState<List[]>([]);
    const [displayUserForm, setDisplayUserForm] = React.useState<boolean>(false);
    const [displayStockForm, setDisplayStockItemForm] = React.useState<boolean>(false);
    const [displayListsForm, setDisplayListsForm] = React.useState<boolean>(false);
    const [settingsScope, setSettingsScope] = React.useState<SettingsScope>("users");
    const [selectedItemId, setSelectedItemId] = React.useState<string | null>(null);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [dialogText, setDialogText] = useState<string>("");

    useEffect(() => {
        loadData();
    }, [settingsScope]);

    const loadData = async () => {
        if (settingsScope === "users") {
            await loadUsers();
        } else if (settingsScope === "stocks") {
            await loadStocks();
        } else if (settingsScope === "airlines") {
            await loadAirlines();
        }
    }

    const loadAirlines = async () => {
        try {
            const res = await axios.post("api/airline/getAll");
            setAirlines(res.data.airlines);
        } catch {
            setDialogText("Letecké společnosti se nepodařilo načíst. Zkuste to prosím znovu.");
            setShowDialog(true);
        }
    };

    const loadStocks = async () => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/stock/getAll",
                    data: {
                        module: selectedModule?.id
                    }
                })
            );

            setStocks(res.data.stocks);
        } catch (err) {
            throw err;
        }
    }

    const loadUsers = async () => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/appuser/getAll",
                })
            );

            setUsersState(res.data.users);
        } catch (err) {
            throw err;
        }
    }

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    };

    const onSaveUser = async (userId: string) => {

        if (!userId) {
            setDisplayUserForm(false);
        } else {
            await loadData();
            setDisplayUserForm(false);
        }
    };

    const onSaveStock = async (stockId: string) => {

        if (!stockId) {
            setDisplayStockItemForm(false);
        } else {
            await loadData();
            setDisplayStockItemForm(false);
        }
    };

    const onClickUpdate = (newItem: boolean) => {
        const api = gridApiRef.current;

        if (!newItem) {
            if (!api || api.isDestroyed()) return;
            const ids = api.getSelectedNodes().map(n => n.data.id);

            if (ids.length > 0) {
                setSelectedItemId(ids[0]);
            } else {
                return;
            }
        } else {
            setSelectedItemId(null);
        }

        if (settingsScope === "users") {
            setDisplayUserForm(true);
        }

        if (settingsScope === "stocks") {
            setDisplayStockItemForm(true);
        }

        if (settingsScope === "airlines") {
            setDisplayAirlineForm(true);
        }
    };

    const handleFileImportEmployesChange = async (event) => {
        const file = event.target.files[0];

        if (!file) return;

        try {
            const buffer = await file.arrayBuffer();

            const employes = await parseFileImportEmployes(buffer);
            await insertEmployes(employes);

            setDialogText("Import zaměstnanců proběhl v pořádku");
            setShowDialog(true);
        } catch (err) {
            console.error("Failed to upload employes", err);

            setDialogText("Import nebylo možné provést, zkontrolujte prosím správnost dat");
            setShowDialog(true);
        }
    };

    const insertEmployes = async (employees: any[]) => {
        for (const employee of employees) {
            const employeeName = `${employee?.lastName} ${employee?.firstName}`;
            const objectTypeid = "1";

            const newEmployeeId = await insertEmployee(employeeName, objectTypeid);

            if (employee?.idA) {
                await insertPersonalNumber(newEmployeeId, employee.idA, false);
            }

            if (employee?.idB) {
                await insertPersonalNumber(newEmployeeId, employee.idB, true);
            }
        }
    }

    const insertEmployee = async (name: string, typeId: string) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/employee/insert",
                data: {
                    name: name,
                    typeid: typeId
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to insert employee item", err);
            throw err;
        }
    };

    const insertPersonalNumber = async (objectId: string, personalNumber: string, main: boolean) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/employee/insertPersonalId",
                data: {
                    objectid: objectId,
                    personalnumber: personalNumber,
                    main: main
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to insert personal number item", err);
            throw err;
        }
    };

    const parseFileImportEmployes = async (arrayBuffer: any) => {

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const worksheet = workbook.worksheets[0];
        if (!worksheet) return [];

        const parsed = [];
        const startRow = 2;

        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            if (rowNumber < startRow) return;

            const idA = row.getCell(1).text?.trim();
            const idB = row.getCell(2).text?.trim();
            const lastName = row.getCell(3).text?.trim();
            const firstName = row.getCell(4).text?.trim();

            if (!idA && !idB && !lastName && !firstName) return;

            parsed.push({ idA, idB, lastName, firstName, rowNumber });
        });

        return parsed;
    }

    return <>
        {displayUserForm && (
            <div className="inv-modal-overlay1" onMouseDown={() => setDisplayUserForm(false)}>
                <div
                    className="inv-modal1"
                    onMouseDown={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                >
                    <AppUserCardOdmraz userId={selectedItemId} onSave={onSaveUser}></AppUserCardOdmraz>
                </div>
            </div>
        )}

        {displayStockForm && (
            <div className="inv-modal-overlay1" onMouseDown={() => setDisplayStockItemForm(false)}>
                <div
                    className="inv-modal1"
                    onMouseDown={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                >
                    <StockCard stockId={selectedItemId} onSave={onSaveStock}></StockCard>
                </div>
            </div>
        )}

        {displayAirlineForm && (
            <div className="inv-modal-overlay1">
                <div className="inv-modal1" role="dialog" aria-modal="true" aria-label="Letecká společnost">
                    <AirlineCard
                        airline={airlines.find(airline => String(airline.id) === String(selectedItemId))}
                        onCancel={() => setDisplayAirlineForm(false)}
                        onSave={() => {
                            setDisplayAirlineForm(false);
                            void loadAirlines();
                        }}
                    />
                </div>
            </div>
        )}

        {showDialog &&
            <DialogWindow
                displayDialog={showDialog}
                setDisplayDialog={setShowDialog}
                title={dialogText}
                cancelText={"OK"}
            />
        }
        {!displayUserForm && !displayStockForm && !displayAirlineForm && !showDialog && <Card
            title="Nastavení"
        >
            <div className="item-list-card">
                <div className="item-list-toolbar">
                    <button
                        type="button"
                        className={`item-list-tab ${settingsScope === "users" ? "is-active" : ""}`}
                        onClick={() => { setSettingsScope("users"); }}
                    >
                        Uživatelé
                    </button>
                    <button
                        type="button"
                        className={`item-list-tab ${settingsScope === "stocks" ? "is-active" : ""}`}
                        onClick={() => { setSettingsScope("stocks"); }}
                    >
                        Sklady
                    </button>

                    <button
                        type="button"
                        className={`item-list-tab ${settingsScope === "airlines" ? "is-active" : ""}`}
                        onClick={() => { setSettingsScope("airlines"); }}
                    >
                        Letecké společnosti
                    </button>

                    <div className="item-list-toolbar-right">
                        <button
                            type="button"
                            className={`item-list-tab is-active`}
                            onClick={() => onClickUpdate(false)}
                        >
                            Upravit
                        </button>

                        <button
                            type="button"
                            className={`item-list-tab is-active`}
                            onClick={() => onClickUpdate(true)}
                        >
                            Nový
                        </button>
                    </div>
                </div>

                <div className="item-list-grid-wrapper">
                    {settingsScope === "users" && (
                        <AgGridReact<User>
                            theme={myTheme}
                            localeText={AG_GRID_LOCALE_CZ}
                            columnDefs={columnsUsers}
                            rowData={usersState}
                            pagination={false}
                            rowSelection="single"
                            onGridReady={onGridReady}
                        />
                    )}

                    {settingsScope === "stocks" && (
                        <AgGridReact<Stock>
                            theme={myTheme}
                            localeText={AG_GRID_LOCALE_CZ}
                            columnDefs={columnsStocks}
                            rowData={stocks}
                            pagination={false}
                            rowSelection="single"
                            onGridReady={onGridReady}
                        />
                    )}
                    {settingsScope === "airlines" && (
                        <AgGridReact<Airline>
                            theme={myTheme}
                            localeText={AG_GRID_LOCALE_CZ}
                            columnDefs={columnsAirlines}
                            rowData={airlines}
                            pagination={false}
                            rowSelection="single"
                            onGridReady={onGridReady}
                        />
                    )}
                </div>

                <input
                    type="file"
                    ref={fileImportEmployesInputRef}
                    style={{ display: "none" }}
                    accept=".xlsx"
                    onChange={handleFileImportEmployesChange}
                />

                {
                    actions?.length ? (
                        <div className="item-list-actions">
                            <FloatingActions actions={actions} />
                        </div>
                    ) : null
                }
            </div ></Card>}
    </>;
};

export default userSettingsOdmraz;
